import { useEffect, useState } from 'react';
import { Alert, Button, Center, Loader, Stack, Text, Title } from '@mantine/core';
import { CheckCircle2, XCircle } from 'lucide-react';
import type { InstallMessage } from './InstallPopupHelper';

type CallbackStatus = 'pending' | 'success' | 'error';

function postToOpener(message: InstallMessage): void {
    if (window.opener && !window.opener.closed) {
        window.opener.postMessage(message, window.location.origin);
    }
}

export default function InstallCallbackPage(): React.ReactElement {
    const [status, setStatus] = useState<CallbackStatus>('pending');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const allParams: Record<string, string> = {};
        params.forEach((v, k) => {
            allParams[k] = v;
        });

        const providerError = params.get('error_description') ?? params.get('error');
        if (providerError) {
            postToOpener({ type: 'install:error', error: providerError });
            setStatus('error');
            setErrorMessage(providerError);
            return;
        }

        if (!params.get('state')) {
            const message = 'Install callback is missing the required `state` parameter.';
            postToOpener({ type: 'install:error', error: message });
            setStatus('error');
            setErrorMessage(message);
            return;
        }

        postToOpener({ type: 'install:complete', params: allParams });
        setStatus('success');
        window.setTimeout(() => window.close(), 600);
    }, []);

    return (
        <Center h="100vh" p="md">
            <Stack align="center" gap="md" maw={420}>
                {status === 'pending' && (
                    <>
                        <Loader />
                        <Title order={4}>Finalizing install…</Title>
                        <Text size="sm" c="dimmed">Please keep this window open for a moment.</Text>
                    </>
                )}
                {status === 'success' && (
                    <Alert variant="light" color="green" icon={<CheckCircle2 size={16} />} title="Installed">
                        <Text size="sm">Install complete. You can close this window.</Text>
                    </Alert>
                )}
                {status === 'error' && (
                    <Stack align="stretch" gap="sm" w="100%">
                        <Alert variant="light" color="red" icon={<XCircle size={16} />} title="Install failed">
                            <Text size="sm">{errorMessage}</Text>
                        </Alert>
                        <Button variant="default" onClick={() => window.close()}>Close window</Button>
                    </Stack>
                )}
            </Stack>
        </Center>
    );
}
